import { useMemo } from 'react';
import { pick } from 'lodash/fp';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';

import {
  LabelValueDetails,
  LabelValue,
  TimeSignal,
  LabelValueSource,
  LabelSuggestionDetails
} from 'graphql/types';
import { useFeatureFlag } from 'modules/common/hooks/useFeatureFlag';
import { useIsSmartSuggestionSettingEnabled } from './useIsSmartSuggestionSettingEnabled';
import { determineLabelAcceptedBy } from './utils';

export const picker = pick(['idValue', 'numericValue', 'stringValue']);
export const useCollatedTimesheetLabels = (
  timeSignal: TimeSignal,
  labels: TimesheetLabel[]
): TimesheetLabel[] => {
  const labelValues = timeSignal?.labelValues as LabelValueDetails[];
  const suggestions = timeSignal?.suggestions
    ?.suggestions as LabelSuggestionDetails[];
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  return useMemo(
    () =>
      (labels || []).map((label: TimesheetLabel) => {
        const labelValue = labelValues?.find(
          (lv) => lv?.label?.id == label?.id
        );
        const suggestion = suggestions?.find(
          (suggestion: LabelSuggestionDetails) =>
            suggestion?.label?.id == label?.id
        );
        const returnValue = {
          ...label,
          value: labelValue?.value ? picker(labelValue.value) : null
        };
        if (isSmartSuggestionEnabled) {
          const suggestedValue = isTimeSignalFederatedSuggestionsEnabled
            ? (suggestion?.suggestedValues?.map(picker) as [LabelValue])
            : labelValue?.suggestedValues?.map(picker);
          const suggestionMetadata = isTimeSignalFederatedSuggestionsEnabled
            ? suggestion?.suggestionMetadata
            : labelValue?.suggestionMetadata;
          returnValue.valueSource = labelValue?.valueSource;
          returnValue.suggestedValues = suggestedValue ?? undefined;
          returnValue.suggestionMetadata = suggestionMetadata ?? undefined;
          returnValue.thresholdMet = isTimeSignalFederatedSuggestionsEnabled
            ? suggestion?.thresholdMet ?? false
            : false;
        }
        returnValue.acceptedBy = determineLabelAcceptedBy(
          returnValue.valueSource ?? LabelValueSource.User,
          labelValue?.acceptedBy,
          isSmartSuggestionEnabled
        );
        return returnValue;
      }),
    [
      labels,
      labelValues,
      suggestions,
      isSmartSuggestionEnabled,
      isTimeSignalFederatedSuggestionsEnabled
    ]
  );
};

export default useCollatedTimesheetLabels;
